import { Component, inject, output, signal } from '@angular/core';
import { CodeVerificationService, VERIFICATION_CODE_LENGTH } from './code-verification.service';

/**
 * The code screen of the gift-card purchase without a session (DEV-11124 / ELIO-6959).
 *
 * Everything it needs is in `CodeVerificationService`, so wiring it into a brand's purchase
 * component is one tag and one handler: render it while `verification.pending()` is true and
 * resume the purchase on `verified`. Nothing here is specific to a gateway, so the eleven brands
 * share it whichever one they use.
 */
@Component({
  selector: 'app-code-verification',
  imports: [],
  template: `
    <section class="text-center py-4 animated fadeInUp">
      <i class="fas fa-envelope-open-text fa-3x mb-3 text-secondary-800"></i>
      <h2 class="h5-bold text-secondary-800 head-font">Verify your email</h2>
      <p class="h6-regular text-secondary mt-2">
        We've sent a {{ length }}-digit code to <strong>{{ verification.email() }}</strong>.<br />
        Enter it to complete your purchase — it expires in 10 minutes. Don't forget to check your spam folder.
      </p>

      <div class="d-flex justify-content-center mt-4">
        <input class="form-control text-center code-input" [class.is-invalid]="!!verification.error()"
          type="text" inputmode="numeric" autocomplete="one-time-code" [attr.maxlength]="length"
          [value]="code()" (input)="onCodeInput($event)" (keyup.enter)="submit()"
          [disabled]="verification.isChecking()" aria-label="Verification code" />
      </div>

      @if (verification.error()) {
      <p class="h7-regular text-danger mt-2 mb-0">{{ verification.error() }}</p>
      }

      <div class="d-grid col-10 mx-auto mt-4">
        <button type="button" class="btn btn-primary" (click)="submit()"
          [disabled]="verification.isChecking() || code().length !== length">
          {{ verification.isChecking() ? 'Verifying…' : 'Verify and continue' }}
        </button>
      </div>

      <button type="button" class="resend link-color h7-bold cursor-pointer mt-3" (click)="verification.resend()"
        [disabled]="verification.resendCooldown() > 0 || verification.isSending()">
        @if (verification.resendCooldown() > 0) {
        Resend code ({{ verification.resendCooldown() }}s)
        } @else {
        Resend code
        }
      </button>

      <p class="h7-regular text-secondary mt-2 mb-0">
        Wrong address? Cancel and edit the sender's email.
      </p>
    </section>
  `,
  styles: `
    .code-input {
      max-width: 12rem;
      letter-spacing: .5rem;
      font-size: 1.5rem;
    }
    /* A link, not a button: Bootstrap's .btn-link paints its own blue over the brand's
       .link-color, and this sits next to the signup's «Change email», which is styled this way. */
    .resend {
      background: none;
      border: 0;
      padding: 0;
      font: inherit;
      text-decoration: underline;
    }
    .resend:disabled {
      opacity: .55;
      text-decoration: none;
      cursor: default;
    }
  `
})
export class CodeVerificationComponent {

  verification = inject(CodeVerificationService);

  /** The sender proved the address: the caller can go back to charging the card. */
  verified = output<void>();

  length = VERIFICATION_CODE_LENGTH;

  code = signal('');

  onCodeInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    // Pasting the code from the email drags spaces and the odd stray character with it.
    const digits = input.value.replace(/\D/g, '').slice(0, this.length);
    this.code.set(digits);
    input.value = digits;
  }

  submit(): void {
    if (this.verification.isChecking() || this.code().length !== this.length) return;

    this.verification.check(this.code()).subscribe(verified => {
      if (!verified) return this.code.set('');
      this.verified.emit();
    });
  }
}
